<script setup lang="ts" generic="T">
    // Ignores behövs, pga kaosiga versioner av TS, Vite, Vue osv...
    // @ts-ignore
    import { EventInput } from "@fullcalendar/core";
    // Ignores behövs, pga kaosiga versioner av TS, Vite, Vue osv...
    // @ts-ignore
    import { Draggable } from "@fullcalendar/interaction";
    import moment from "moment";
    import { computed, onMounted, onUnmounted, ref, toRefs } from "vue";

    // Hårdkodad typning som inte är exporterad, pga kan inte ha externa typer innan Vue3
    interface CalendarDropAreaProps<T> {
        id: string;
        events: EventInput[];
        filter: (props: { event: EventInput }) => boolean;
        data?: Map<string, T>;
        colorKey?: string;
        viewType?: string;
        /*
            Ska tid och datum som eventet kan släppas på vara låsta till det satt i extendedProps.restrictedDates?
            Om true och restrictedDates är satt kommer få fasta datum och kommer ignorera exakt tidpunkt som eventet
            släppts på
         */
        restrictDates?: boolean;
        title?: string;
        /*
            Objekt med key som är namn på möjligt namn som kan användas som color key och Map som innehåller
            key id som ska kollas mot och value index som ska användas vid hämtning av färg
            T.ex: { vardgivare: new Map([["abcdef", 1], ["glqer", 7]]) }
         */
        colorKeyIndices?: { [key: string]: Map<string, number> };
    }

    const props = withDefaults(defineProps<CalendarDropAreaProps<T>>(), {
        restrictDates: false,
        title: "title",
        id: "calendar-drop-area-id",
    });

    const { events, id, filter, data, viewType, restrictDates } = toRefs(props);

    const dropArea = ref();
    const draggable = ref();

    const filteredEvents = computed(() => events.value.filter((event) => filter.value({ event })));

    const setDraggable = () => {
        draggable.value = new Draggable(dropArea.value, {
            itemSelector: ".fc-event",
            appendTo: document.getElementById(String(id.value)) as NonNullable<HTMLHtmlElement>,
            eventData: (eventElement) => {
                const dataAttribute = eventElement.getAttribute("data-event");

                if (dataAttribute) {
                    const eventData = JSON.parse(dataAttribute);
                    const durationMinutes = moment(eventData.end).diff(eventData.start, "minutes");

                    const extendedProps = { ...eventData.extendedProps };

                    if (restrictDates.value) {
                        extendedProps.restrictedDates = {
                            start: eventData.start,
                            end: eventData.end,
                        };
                    }

                    return {
                        ...eventData,
                        duration:
                            eventData.duration ??
                            `${String(Math.floor(durationMinutes / 60)).padStart(2, "0")}:${String(
                                durationMinutes % 60
                            ).padStart(2, "0")}`,
                        extendedProps: {
                            ...extendedProps,
                        },
                    };
                }

                return null;
            },
        });
    };

    onMounted(() => {
        setDraggable();
    });

    onUnmounted(() => {
        draggable.value.destroy();
    });
</script>

<template>
    <div ref="dropArea" :id="id" class="calendar-drop-area">
        <div v-if="title" class="title">
            <span>{{ title }}</span>
        </div>
        <div class="inner">
            <a
                v-for="event in filteredEvents"
                :key="event.id"
                class="event fc-event calendar-drop-area-event"
                :data-event="JSON.stringify(event)"
            >
                <slot
                    name="event"
                    :event="event"
                    :data="data?.get(event.id ?? '')"
                    :colorKey="colorKey"
                    :viewType="viewType"
                    :colorKeyIndices="colorKeyIndices"
                />
            </a>
        </div>
    </div>
</template>

<style scoped lang="scss">
    @import "@/style/variables";

    .calendar-drop-area {
        $padding: 0.8rem;

        display: flex;
        flex-wrap: wrap;
        gap: 1rem;
        background-color: $color-background-tool-menu;
        padding: $padding;
        border-radius: $border-radius;
        width: 100%;
        max-width: 100%;
        box-sizing: border-box;
        min-height: fit-content;

        .title {
        }

        .inner {
            display: grid;
            grid-auto-flow: row;
            grid-template-columns: repeat(auto-fit, 6rem);
            grid-gap: 0.5rem;
            max-width: 100%;
        }

        ::v-deep .fc-event {
            .k-event {
                position: relative;

                &:hover {
                    min-width: fit-content;
                    overflow: visible;
                    z-index: 10;
                    box-shadow: $dropdown-box-shadow;
                }
            }
        }
    }
</style>

<style lang="scss">
    // Behövs för att events som dras in inte expanderar storleken på korta events för att visa allt innehåll
    .calendar-drop-area-event.fc-event-dragging {
        &:hover {
            all: unset;
            pointer-events: none;
        }
    }
</style>
